/**
 * CRM Card Preview Component
 * UX Redesign: Shows a visual preview of how AICA data will appear in the CRM card
 */
import { Star, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

interface CrmCardPreviewProps {
  /** Which fields are enabled for display */
  enabledFields: {
    qualityScore: boolean;
    transcript: boolean;
    summary: boolean;
    saleProbability: boolean;
  };
  /** Sample quality score to display (0-100) */
  sampleScore?: number;
}

export function CrmCardPreview({
  enabledFields,
  sampleScore = 8.2,
}: CrmCardPreviewProps) {
  const { t } = useTranslation(['dashboard', 'common']);

  // Generate star rating (0-5 stars based on 0-10 score)
  const starCount = Math.round((sampleScore / 100) * 5);
  const stars = Array.from({ length: 5 }, (_, i) => i < starCount);

  // Check if any field is enabled
  const hasAnyField = Object.values(enabledFields).some(Boolean);

  if (!hasAnyField) {
    return null;
  }

  return (
    <div className="rounded-lg border border-border bg-bg-surface p-3">
      {/* Preview label */}
      <p className="text-xs text-fg-muted mb-2">{t('embedded.preview')}</p>

      {/* Simulated CRM card */}
      <div className="rounded-md border border-bg-elevated bg-bg-surface p-3">
        {/* Card header */}
        <div className="text-xs font-semibold text-fg mb-2">
          {t('embedded.aicaAnalysis')}
        </div>

        {/* Quality Score */}
        {enabledFields.qualityScore && (
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs text-fg-muted">{t('embedded.qualityLabel')}:</span>
            <div className="flex items-center gap-0.5">
              {stars.map((filled, index) => (
                <Star
                  key={`star-${index}`}
                  className={cn(
                    'h-3 w-3',
                    filled ? 'fill-primary text-primary' : 'text-fg-subtle'
                  )}
                />
              ))}
            </div>
            <span className="text-xs font-medium text-fg tabular-nums">
              {sampleScore.toFixed(1)}/100
            </span>
          </div>
        )}

        {/* Sale Probability */}
        {enabledFields.saleProbability && (
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs text-fg-muted">
              {t('embedded.saleProbabilityField')}:
            </span>
            <span className="text-xs font-medium text-success">72%</span>
          </div>
        )}

        {/* Summary / Key Moments */}
        {enabledFields.summary && (
          <div className="mb-2">
            <span className="text-xs text-fg-muted">
              {t('embedded.keyMomentsField')}:
            </span>
            <div className="mt-1 text-xs text-fg-muted italic">"..."</div>
          </div>
        )}

        {/* Transcript toggle */}
        {enabledFields.transcript && (
          <button
            type="button"
            className={cn(
              'flex items-center gap-1 text-xs text-primary',
              'hover:text-primary-hover transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded'
            )}
          >
            {t('embedded.showTranscript')}
            <ChevronDown className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
}
